import { useEffect } from 'react'
import { useLoaderData } from 'react-router'
import { useInView } from 'react-intersection-observer'
import { Flex } from '@radix-ui/themes'
import { PageHeading } from '@/components/subscription/page-heading'
import { formatDate } from '@/lib/shipment'
import { yen } from '@/lib/price'
import { DeliverySummary, toDeliverySummary } from '@/lib/order'
import { fetchSubscriptionOrderedPlacements, useSubscriptionOrderedPlacements } from '@/hooks/use-subscription-ordered-placements'
import * as styles from './styles.css'

export const loader = async () => {
  return await fetchSubscriptionOrderedPlacements('lossless_bouquet', 1)
}

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => {
  return (
    <div className={styles.field}>
      <span className={styles.label}>{label}</span>
      <div className={styles.value}>{children}</div>
    </div>
  )
}

const DeliveryCard: React.FC<{ delivery: DeliverySummary }> = ({ delivery }) => {
  return (
    <div className={styles.card}>
      {(delivery.imageUrl || delivery.description) && (
        <div className={styles.flowerInfo}>
          {delivery.imageUrl && <img src={delivery.imageUrl} alt="" className={styles.photo} />}
          {delivery.description && <p className={styles.description}>{delivery.description}</p>}
        </div>
      )}
      <div className={styles.divider} />
      <div className={styles.fields}>
        {delivery.deliverAt && <Field label="お届け日">{formatDate(delivery.deliverAt * 1000, 'yyyy年M月d日（E）')}</Field>}
        {delivery.address && (
          <Field label="お届け先">
            <p>
              {delivery.address.postalCode && `〒${delivery.address.postalCode} `}
              {delivery.address.address}
            </p>
            {delivery.address.name && <p>{delivery.address.name}</p>}
          </Field>
        )}
        <Field label="合計金額">{yen(delivery.total)}（税込）</Field>
      </div>
      {delivery.invoiceUrl && (
        <a href={delivery.invoiceUrl} target="_blank" rel="noopener noreferrer" className={styles.invoiceLink}>
          領収書を表示する
        </a>
      )}
    </div>
  )
}

export const SubscriptionMypageOrders: React.FC = () => {
  const {
    data: { data: initialPlacements, included: initialIncluded },
  } = useLoaderData<typeof loader>()

  const { placements, included, hasNextPage, loadMore } = useSubscriptionOrderedPlacements({
    category: 'lossless_bouquet',
    initialPlacements,
    initialIncluded,
  })

  const deliveries = placements
    .map((placement) => ({ id: placement.id, summary: toDeliverySummary(placement, included) }))
    .filter((delivery): delivery is { id: string; summary: DeliverySummary } => delivery.summary !== null)

  const { ref, inView } = useInView({ rootMargin: '300px' })

  useEffect(() => {
    if (inView) {
      loadMore()
    }
  }, [inView, loadMore])

  return (
    <Flex direction="column" align="center" gap="32px">
      <PageHeading>お届け履歴</PageHeading>
      {deliveries.length === 0 ? (
        <div className={styles.card}>
          <p className={styles.text}>お届け履歴はありません</p>
        </div>
      ) : (
        <Flex direction="column" align="center" gap="16px" width="100%">
          {deliveries.map((delivery) => (
            <DeliveryCard key={delivery.id} delivery={delivery.summary} />
          ))}
        </Flex>
      )}
      {hasNextPage && (
        <div className={styles.sentinel} ref={ref}></div>
      )}
    </Flex>
  )
}
